import { useEffect } from 'react';

import { wogaaEnabled } from '@shared/configs/configs/config';
import { useGetSystemSettingsQuery } from '@shared/services/systemSettings';
import type { WogaaRouteConfig } from '@shared/types/common.types';

/**
 * Fires the WOGAA transactional-service tracking call for a route, if configured.
 *
 * Owns the full resolution of a route's `wogaa` config end-to-end: given the route-level
 * `WogaaRouteConfig` (`role` + `settingKey`), it looks up the actual `trackingId` from System
 * Settings itself and fires the tracking call — callers (e.g. `RouteWrapper`) don't need any
 * System Settings lookup logic of their own, they just pass the route's `wogaa` config through.
 *
 * Mirrors the `useInitGTM` pattern: it is a no-op (does not throw) when the feature flag is
 * disabled, when the route has no `wogaa` config, when the System Settings `trackingId` hasn't
 * resolved yet (or is missing), or when `window.wogaaCustom` is unavailable (e.g. blocked by an
 * adblocker).
 */
const useWOGAA = (wogaa?: WogaaRouteConfig) => {
  const { data: wogaaSystemSettings } = useGetSystemSettingsQuery(
    { Categories: wogaa ? [wogaa.settingKey] : [] },
    { skip: !wogaa }
  );
  const trackingId = wogaaSystemSettings?.find((setting) => setting.Category === wogaa?.settingKey)?.Value;

  useEffect(() => {
    if (!wogaaEnabled() || !wogaa?.role || !trackingId) return;

    const { wogaaCustom } = window;
    if (!wogaaCustom) return;

    const trackingFn = wogaa.role === 'start' ? wogaaCustom.startTransactionalService : wogaaCustom.completeTransactionalService;

    trackingFn(trackingId);
    // Depend on the primitive fields instead of `wogaa` by reference: callers (e.g.
    // `RouteWrapper`) may pass a brand-new object literal on every render, and depending on the
    // object reference would re-fire this effect (and duplicate the WOGAA transactional-service
    // call) on every unrelated re-render instead of only when `role`/`trackingId` actually change.
  }, [wogaa?.role, trackingId]);
};

export default useWOGAA;
